import { type FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/app-store';
import { Badge } from '@/components/ui/Badge';
import { ASIA_FOCUS_COUNTRIES } from '@/config/countries';
import type { ThreatLevel } from '@/types';

// ---------------------------------------------------------------------------
// Asia Deep Analysis Panel — Risk scores + Asia-specific news feed
// ---------------------------------------------------------------------------

type Tab = 'risk' | 'news';

function scoreToLevel(score: number): ThreatLevel {
  if (score >= 70) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export const AsiaIntelPanel: FC = () => {
  const { t, i18n } = useTranslation();
  const countryRisks = useAppStore((s) => s.countryRisks);
  const asiaNews = useAppStore((s) => s.asiaNews);
  const [tab, setTab] = useState<Tab>('risk');

  const asiaRisks = useMemo(
    () => countryRisks
      .filter((c) => ASIA_FOCUS_COUNTRIES.includes(c.code))
      .sort((a, b) => b.score - a.score),
    [countryRisks],
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="panel-header flex items-center justify-between shrink-0">
        <span className="panel-title">{t('panels.asiaIntel')}</span>
        <div className="flex gap-1">
          <button
            onClick={() => setTab('risk')}
            className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
              tab === 'risk'
                ? 'bg-brand/20 text-brand-light'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            Risk
          </button>
          <button
            onClick={() => setTab('news')}
            className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
              tab === 'news'
                ? 'bg-brand/20 text-brand-light'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            News
            {asiaNews.length > 0 && (
              <span className="ml-1 text-[9px] text-slate-500">{asiaNews.length}</span>
            )}
          </button>
        </div>
      </div>

      <div className="panel-body flex-1 overflow-y-auto space-y-1">
        {tab === 'risk' && (
          <>
            {asiaRisks.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">No Asia risk data</p>
            )}
            {asiaRisks.map((c) => (
              <div key={c.code} className="data-row justify-between">
                <span className="text-xs text-slate-300">
                  {i18n.language === 'ko' && c.nameKo ? c.nameKo : c.name}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs tabular-nums font-semibold text-slate-200">{c.score}</span>
                  <Badge level={scoreToLevel(c.score)} />
                </div>
              </div>
            ))}
          </>
        )}

        {tab === 'news' && (
          <>
            {asiaNews.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">Loading Asia feeds...</p>
            )}
            {asiaNews.slice(0, 20).map((item) => (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="data-row flex-col items-start gap-0.5 hover:bg-white/5 transition-colors"
              >
                <span className="text-xs text-slate-200 line-clamp-2 leading-snug">
                  {item.title}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-brand-light/70">{item.source}</span>
                  <span className="text-[10px] text-slate-600">{timeAgo(item.publishedAt)}</span>
                </div>
              </a>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
